import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { it } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import type { ConferimentoListItem } from '../../api/conferimenti';
import type { GiornoCalendario } from '../../api/programmazione';
import { fmtKg } from '../../lib/saldo';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales: { it },
});

const STATUS_COLORS: Record<string, string> = {
  programmato: '#3B82F6',
  in_ritardo: '#EF4444',
  in_transito: '#06B6D4',
  attesa_compliance: '#F59E0B',
  in_stoccaggio: '#10B981',
  respinto: '#8B5CF6',
};

function satBgColor(pct: number, isChiuso: boolean): string {
  if (isChiuso) return '#e5e7eb';
  if (pct >= 100) return '#fca5a5';
  if (pct >= 80) return '#fee2e2';
  if (pct >= 50) return '#fef3c7';
  if (pct > 0) return '#dcfce7';
  return '';
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: ConferimentoListItem;
}

interface Props {
  items: ConferimentoListItem[];
  saturazioni?: Record<string, GiornoCalendario>;
  onSelectDay?: (date: string) => void;
  onNavigate?: (date: Date) => void;
}

export default function ConferimentiCalendarioView({ items, saturazioni, onSelectDay, onNavigate }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const events: CalendarEvent[] = items
    .filter((c) => c.data_prevista)
    .map((c) => {
      const d = new Date(c.data_prevista! + 'T00:00:00');
      return {
        id: c.id,
        title: `${c.numero ?? '—'} — ${c.customer_ragione_sociale} (${c.peso_stimato_kg ? fmtKg(c.peso_stimato_kg) + ' kg' : '?'})`,
        start: d,
        end: d,
        allDay: true,
        resource: c,
      };
    });

  function toISO(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  return (
    <div className="h-[680px]">
      <Calendar
        localizer={localizer}
        events={events}
        views={['month', 'week', 'day']}
        defaultView="month"
        culture="it"
        onNavigate={(date) => onNavigate?.(date)}
        onSelectEvent={(event) => navigate(`/conferimenti/${event.resource.id}`)}
        onSelectSlot={(slot) => {
          if (onSelectDay && slot.start) {
            onSelectDay(toISO(new Date(slot.start)));
          }
        }}
        selectable
        dayPropGetter={(date) => {
          if (!saturazioni) return {};
          const iso = toISO(date);
          const g = saturazioni[iso];
          if (!g) return {};
          const bg = satBgColor(g.saturazione_pct, g.impianto_chiuso);
          if (!bg) return {};
          return { style: { backgroundColor: bg } };
        }}
        eventPropGetter={(event) => {
          const statusKey = event.resource.status_display;
          const color = STATUS_COLORS[statusKey] ?? '#6B7280';
          const isRespinto = event.resource.status === 'respinto';
          return {
            style: {
              backgroundColor: color,
              borderColor: color,
              color: '#fff',
              fontSize: '11px',
              borderRadius: '4px',
              ...(isRespinto && {
                textDecoration: 'line-through',
                opacity: 0.5,
              }),
            },
          };
        }}
        messages={{
          next: t('conferimenti.calendario.next'),
          previous: t('conferimenti.calendario.previous'),
          today: t('conferimenti.calendario.today'),
          month: t('conferimenti.calendario.month'),
          week: t('conferimenti.calendario.week'),
          day: t('conferimenti.calendario.day'),
          agenda: t('conferimenti.calendario.agenda'),
          date: t('conferimenti.calendario.date'),
          time: t('conferimenti.calendario.time'),
          event: t('conferimenti.calendario.event'),
          noEventsInRange: t('conferimenti.calendario.no_events'),
        }}
      />
    </div>
  );
}
